import {
  BANNER_POSITION,
  CORNER_OPTION,
  SLIDE_ANIMATION,
  WIDGET_POSITION,
  FONT_FAMILY_OPTIONS,
  TOOL_BANNER,
  TOOL_WIDGET,
  TOOL_OFFERWALL,
  TOOL_PAYWALL,
} from '@shared/types'
import type {
  BannerProfile,
  OfferwallProfile,
  PaywallProfile,
  Tool,
  ToolProfile,
  WidgetProfile,
} from '@shared/types'

export const createDefaultBannerProfile = (
  profileName: string,
): BannerProfile => {
  const now = new Date().toISOString()
  return {
    $version: '0.0.1',
    $name: profileName,
    $modifiedAt: now,
    title: {
      text: 'Supported by visitors like you!',
    },
    description: {
      text: 'The Web Monetization browser extension lets you set a monthly budget and automatically pay it out to the websites you enjoy. It’s simple, secure, and helps keep this content online.',
      isVisible: true,
    },
    font: {
      name: FONT_FAMILY_OPTIONS[0],
      size: 'base',
    },
    animation: {
      type: SLIDE_ANIMATION.Slide,
    },
    position: BANNER_POSITION.Bottom,
    border: {
      type: CORNER_OPTION.Light,
    },
    color: {
      text: '#ffffff',
      background: '#7f76b2',
    },
    thumbnail: {
      value: 'default',
    },
  }
}

export const createDefaultWidgetProfile = (
  profileName: string,
): WidgetProfile => ({
  $version: '0.0.1',
  $name: profileName,
  $modifiedAt: '',
  title: {
    text: 'Future of support',
  },
  description: {
    text: 'Experience the new way to support our content. Activate Web Monetization in your browser and support our work as you browse. Every visit helps us keep creating the content you love! You can also support us by a one time donation below!',
    isVisible: true,
  },
  font: {
    name: FONT_FAMILY_OPTIONS[0],
    size: 'base',
  },
  position: WIDGET_POSITION.Right,
  border: {
    type: CORNER_OPTION.Light,
  },
  color: {
    text: '#676767',
    background: '#ffffff',
    theme: '#56b7b5',
  },
  ctaPayButton: {
    text: 'Support me',
  },
  icon: {
    value: '',
    color: '#fff',
  },
})

export const createDefaultPaywallProfile = (
  profileName: string,
): PaywallProfile => {
  return {
    $version: '0.0.1',
    $name: profileName,
    $modifiedAt: '',
    behavior: {
      coverage: { value: 50, enabled: true },
      delay: { value: 0, enabled: true },
    },
    price: { value: '2.00', currency: 'USD' },
    title: {
      text: 'Finish reading this story',
    },
    description: {
      text: `Unlock the full article with a one-time payment - no subscription, no account.`,
    },
    ctaButton: {
      text: 'Unlock article',
    },
    font: {
      name: FONT_FAMILY_OPTIONS[0],
      size: 'base',
    },
    border: {
      type: CORNER_OPTION.Light,
    },
    colors: {
      text: '#363636',
      background: '#ffffff',
      theme: '#56b7b5',
    },
  }
}

export const createDefaultOfferwallProfile = (
  profileName: string,
): OfferwallProfile => ({
  $version: '0.0.1',
  $name: profileName,
  $modifiedAt: '',
  font: {
    name: FONT_FAMILY_OPTIONS[0],
  },
  border: {
    type: CORNER_OPTION.Light,
  },
  color: {
    text: '#000000',
    background: '#ffffff',
    headline: '#000000',
    theme: '#4ec6c0',
  },
})

export function getDefaultProfile<T extends Tool>(tool: T): ToolProfile<T> {
  switch (tool) {
    case TOOL_BANNER:
      return createDefaultBannerProfile('Default') as ToolProfile<T>
    case TOOL_WIDGET:
      return createDefaultWidgetProfile('Default') as ToolProfile<T>
    case TOOL_PAYWALL:
      return createDefaultPaywallProfile('Default') as ToolProfile<T>
    case TOOL_OFFERWALL:
      return createDefaultOfferwallProfile('Default') as ToolProfile<T>
    default:
      throw new Error('Invalid tool type')
  }
}
